import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { styled } from "styled-components";
import { AuthProvider } from "./contexts/AuthContext";
import GlobalStyle from "./styles/globalStyles";

// React Query
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Components
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import CartDrawer from "./components/Cart/CartDrawer";
import Hero2 from "./components/Hero/Hero2";
import ShopPage from "./components/Shop/ShopPage";
import ShopByCategory from "./components/ShopByCategory/ShopByCategory";
import FeaturedProducts from "./components/FeaturedProducts/FeaturedProducts";
import WhyChoose from "./components/WhyChoose/WhyChoose";
import Testimonials from "./components/Testimonials/Testimonials";
import NewsletterSignup from "./components/NewsletterSignup/NewsletterSignup";
import AboutUs from "./components/AboutUs/AboutUs";
import Contact from "./components/Contact/Contact";
import TermsAndConditions from "./components/TermsAndConditions/TermsAndConditions";
import CheckoutPage from "./components/CheckoutPage/CheckoutPage";
import OrderConfirmation from "./components/OrderConfirmation/OrderConfirmation";
import Orders from "./components/Orders/Orders";
import SignIn from "./components/Auth/SignIn";
import SignUp from "./components/Auth/SignUp";
import ForgotPassword from "./components/Auth/ForgotPassword";
import AnimatedSection from "./components/Common/AnimatedSection";
import ScrollToTop from "./components/Common/ScrollToTop";
import ProtectedAdminRoute from "./components/Admin/ProtectedAdminRoute";
import AdminLayout from "./components/Admin/AdminLayout";
import ProductsPage from "./components/Admin/ProductsPage";
import OrdersPage from "./components/Admin/OrdersPage";
import ContactMessagesPage from "./components/Admin/ContactMessagesPage";

const AppWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: hidden;
  padding-top: 72px; /* Account for fixed navbar height */

  @media (max-width: 768px) {
    padding-top: 64px;
  }

  @media (max-width: 480px) {
    padding-top: 60px;
  }
`;

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <GlobalStyle />
            <AppWrapper>
              <Helmet>
                <title>Craft Bin | Handcrafted with Heart</title>
                <meta
                  name="description"
                  content="Explore personalized jewelry, charming pet accessories, bespoke home decor, and more. Handcrafted with heart, created for you."
                />
                <meta
                  name="viewport"
                  content="width=device-width, initial-scale=1"
                />
              </Helmet>
              <Navbar />
              <CartDrawer />
              <Routes>
                <Route
                  path="/"
                  element={
                    <>
                      <Hero2 />
                      <AnimatedSection animation="fadeInUp" delay={200}>
                        <FeaturedProducts />
                      </AnimatedSection>
                      <AnimatedSection animation="fadeInLeft" delay={100}>
                        <ShopByCategory />
                      </AnimatedSection>
                      <AnimatedSection animation="fadeInRight" delay={150}>
                        <WhyChoose />
                      </AnimatedSection>
                      <AnimatedSection animation="fadeInUp" delay={200}>
                        <Testimonials />
                      </AnimatedSection>
                      <AnimatedSection animation="scaleIn" delay={100}>
                        <NewsletterSignup />
                      </AnimatedSection>
                    </>
                  }
                />
                <Route path="/shop" element={<ShopPage />} />
                <Route
                  path="/aboutus"
                  element={
                    <AnimatedSection animation="fadeInUp" delay={100}>
                      <AboutUs />
                    </AnimatedSection>
                  }
                />
                <Route
                  path="/contact"
                  element={
                    <AnimatedSection animation="fadeInUp" delay={100}>
                      <Contact />
                    </AnimatedSection>
                  }
                />
                <Route
                  path="/terms-and-conditions"
                  element={
                    <AnimatedSection animation="fadeInUp" delay={100}>
                      <TermsAndConditions />
                    </AnimatedSection>
                  }
                />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route
                  path="/order-confirmation"
                  element={<OrderConfirmation />}
                />
                <Route path="/orders" element={<Orders />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* Admin Protected Routes */}
                <Route path="/admin" element={<ProtectedAdminRoute />}>
                  <Route element={<AdminLayout />}>
                    <Route index element={<ProductsPage />} />
                    <Route path="products" element={<ProductsPage />} />
                    <Route path="orders" element={<OrdersPage />} />
                    <Route path="messages" element={<ContactMessagesPage />} />
                  </Route>
                </Route>
              </Routes>
              <Footer />
            </AppWrapper>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
